"use client";

import { PortableText } from "next-sanity";
import { richTextComponent } from "@/sanity/sanityPortableText";
import Image from "@/helpers/Image";
import { CTAButton } from "@/shadcn/ui/button";
import { PageEcommerceSchema } from "@/sanity/pages/PageEcommerceTemplate";
import stars from "@/public/ecommerce/stars.svg";
import { cn } from "@/lib/utils";

export default function EcommerceHeader({
  data,
  className,
}: {
  data: PageEcommerceSchema;
  className?: string;
}) {
  if (!data.headerTitle && !data.headerButton) return null;

  return (
    <section
      className={cn(
        "hidden lg:block w-full bg-white border-b border-black/10 lg:sticky lg:top-0 z-[11]",
        className,
      )}
    >
      <div className="mx-auto w-[min(150rem,100%)] px-[3rem] lg:px-[5rem] flex flex-col lg:flex-row lg:items-center lg:justify-between lg:h-[11rem] py-[2.5rem] lg:py-0 gap-[2rem] lg:gap-[3rem]">
        {/* Left: avatar + title + stats */}
        <div className="flex items-center gap-[2rem] min-w-0">
          {data.headerAvatarImage && (
            <div className="bg-[#f1f1f1] rounded-[0.5rem] w-[6.5rem] h-[6.5rem] overflow-hidden flex-shrink-0">
              <Image
                src={data.headerAvatarImage}
                alt=""
                className="!w-full !h-full object-cover"
              />
            </div>
          )}
          <div className="flex flex-col gap-[0.625rem] min-w-0">
            {data.headerTitle && (
              <div className="richtext richtext-start  prose-p:text-[1.85rem] lg:prose-p:text-[2.375rem] prose-p:leading-[1.15] prose-p:text-black prose-p:tracking-[-0.095rem] prose-p:mb-0 prose-p:font-semibold prose-strong:font-bold prose-strong:not-italic">
                <PortableText
                  value={data.headerTitle}
                  components={richTextComponent}
                />
              </div>
            )}
            <div className="flex flex-wrap items-center gap-x-[2.5rem] gap-y-[0.75rem]">
              {(data.headerRating || data.headerRatingCount) && (
                <div className="flex items-center gap-[1.25rem]">
                  <Image src={stars} alt="stars" className="!w-[10.5rem]" />
                  <p className="mb-0 font-geist-mono font-semibold text-black text-[1.5rem] leading-[1.33] tracking-[-0.06rem] uppercase whitespace-nowrap">
                    {data.headerRating && (
                      <strong className="font-bold">{data.headerRating}</strong>
                    )}
                    {data.headerRatingCount && (
                      <>
                        <span className="font-normal text-black/60"> von </span>
                        <strong className="font-bold">
                          {data.headerRatingCount}
                        </strong>
                        <span className="font-normal text-black/60">
                          Tradern
                        </span>
                      </>
                    )}
                  </p>
                </div>
              )}
              {data.headerPayoutText && (
                <>
                  <div className="hidden lg:block w-px h-[2.25rem] bg-black/10 shrink-0" />
                  <p className="mb-0 font-geist-mono text-black/80 font-bold text-[1.5rem] leading-[1.33] tracking-[-0.06rem] uppercase whitespace-nowrap">
                    {data.headerPayoutText}
                  </p>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="hidden lg:flex flex-col lg:flex-row items-center gap-[3rem]">
          {/* Price display */}
          {(data.headerOriginalPrice ||
            data.headerPrice ||
            data.headerDiscountLabel) && (
            <div className="flex items-center gap-[1.25rem] shrink-0">
              <div className="flex items-center gap-[1.25rem] leading-[1.2] text-black">
                {data.headerOriginalPrice && (
                  <span className="line-through opacity-30 font-normal text-[3rem] tracking-[-0.24rem]">
                    {data.headerOriginalPrice}
                  </span>
                )}
                {data.headerPrice && (
                  <span className="font-semibold text-[4rem] lg:text-[5.125rem] tracking-[-0.5rem]">
                    {data.headerPrice}
                  </span>
                )}
              </div>
              {data.headerDiscountLabel && (
                <div className="relative rounded-full px-[1rem] py-[0.5rem] overflow-hidden ">
                  <div
                    aria-hidden
                    className="absolute inset-0 bg-gradient-to-r from-[#E49400] to-[#b36b00] pointer-events-none shadow-[0_4px_8.3px_0_rgba(255,_255,_255,_0.37)_inset]"
                  />
                  <span className="relative font-geist font-semibold text-[1.75rem] leading-[1.2] tracking-[-0.035rem] text-white whitespace-nowrap">
                    {data.headerDiscountLabel}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* CTA */}
          {data.headerButton && (
            <div className="shrink-0 w-full lg:w-auto">
              <CTAButton data={data.headerButton} />
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
